<template>
  <section class="deleted-orders-page">
    <b-card class="toolbar-card mb-3">
      <div class="toolbar-content">
        <div>
          <h3 class="page-title mb-25">
            Colis supprimé
          </h3>
          <p class="text-muted mb-0">
            Consultez les colis supprimés et leur historique.
          </p>
        </div>
        <div class="search-box">
          <b-form-input
            v-model="search"
            placeholder="BL, téléphone ou destinataire"
            @keyup.enter="fetchDeletedOrders(1)"
          />
          <b-button
            variant="primary"
            :disabled="isLoading"
            @click="fetchDeletedOrders(1)"
          >
            <feather-icon
              icon="SearchIcon"
              size="16"
              class="mr-50"
            />
            Rechercher
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="isLoading || !search"
            @click="resetSearch"
          >
            <feather-icon
              icon="RotateCcwIcon"
              size="16"
              class="mr-50"
            />
            Réinitialiser
          </b-button>
        </div>
      </div>
    </b-card>

    <b-alert
      v-if="error"
      variant="danger"
      show
    >
      {{ error }}
    </b-alert>

    <div
      v-if="isLoading"
      class="loading-state"
    >
      <b-spinner variant="primary" />
      <span>Chargement des colis supprimés...</span>
    </div>

    <div v-else>
      <b-card
        v-if="!orders.length"
        class="empty-card text-center"
      >
        <feather-icon
          icon="TrashIcon"
          size="42"
          class="text-muted mb-1"
        />
        <p class="mb-0 text-muted">
          Aucun colis supprimé trouvé.
        </p>
      </b-card>

      <b-card
        v-for="order in orders"
        :key="order.id"
        class="deleted-order-card mb-2"
      >
        <div class="order-row">
          <div class="order-main">
            <div class="order-title-line">
              <strong>#{{ order.id }}</strong>
              <b-badge
                variant="danger"
                class="ml-1"
              >
                Supprimé
              </b-badge>
            </div>
            <div class="order-meta">
              {{ order.name }} - {{ order.phone1 }}
              <span v-if="order.phone2"> / {{ order.phone2 }}</span>
            </div>
            <div
              v-if="order.external_id"
              class="order-meta external-barcode"
            >
              <span>External barcode:</span> {{ order.external_id }}
            </div>
            <div class="order-meta text-muted">
              {{ getLocation(order) }}
            </div>
          </div>

          <div class="order-summary">
            <div>
              <small>Prix</small>
              <strong>{{ order.price_ttc }} DT</strong>
            </div>
            <div>
              <small>Supprimé le</small>
              <strong>{{ formatDateTime(order.deleted_at) }}</strong>
            </div>
            <div>
              <small>Par</small>
              <strong>{{ order.deleted_by_username || 'Système' }}</strong>
            </div>
          </div>

          <b-button
            variant="outline-primary"
            size="sm"
            @click="toggleHistory(order.id)"
          >
            <feather-icon
              :icon="expandedOrderId === order.id ? 'ChevronUpIcon' : 'ClockIcon'"
              size="16"
              class="mr-50"
            />
            Historique ({{ order.histories ? order.histories.length : 0 }})
          </b-button>
        </div>

        <div
          v-if="expandedOrderId === order.id"
          class="history-panel mt-2"
        >
          <div
            v-if="order.histories && order.histories.length"
            class="history-list"
          >
            <div
              v-for="history in order.histories"
              :key="history.id"
              class="history-item"
            >
              <div
                class="history-dot"
                :class="{ danger: isDeleteHistory(history) }"
              />
              <div>
                <div class="history-title">
                  {{ history.message || 'Événement' }}
                  <small>{{ history.created_at_formatted || formatDateTime(history.created_at) }}</small>
                </div>
                <p
                  v-if="history.description"
                  class="history-description"
                >
                  {{ history.description }}
                </p>
                <b-badge
                  v-if="history.done_by"
                  variant="light-secondary"
                >
                  {{ history.done_by }}
                </b-badge>
              </div>
            </div>
          </div>
          <p
            v-else
            class="text-muted mb-0"
          >
            Aucun historique disponible.
          </p>
        </div>
      </b-card>

      <div
        v-if="count > pageSize"
        class="pagination-row"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="count"
          :per-page="pageSize"
          align="center"
          @input="fetchDeletedOrders"
        />
      </div>
    </div>
  </section>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BFormInput,
  BPagination,
  BSpinner,
} from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'DeletedOrders',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BFormInput,
    BPagination,
    BSpinner,
  },
  data() {
    return {
      orders: [],
      search: '',
      error: null,
      isLoading: false,
      currentPage: 1,
      pageSize: 20,
      count: 0,
      expandedOrderId: null,
    }
  },
  created() {
    this.fetchDeletedOrders(1)
  },
  methods: {
    async fetchDeletedOrders(page = this.currentPage) {
      this.isLoading = true
      this.error = null
      this.currentPage = page

      try {
        const { data } = await axios.get('/api/orders/deleted/', {
          params: {
            page,
            page_size: this.pageSize,
            search: this.search || undefined,
          },
        })
        this.orders = data.results || []
        this.count = data.count || 0
        this.pageSize = data.page_size || this.pageSize
      } catch (error) {
        this.error = error.response?.data?.detail || error.response?.data?.error || 'Impossible de charger les colis supprimés.'
      } finally {
        this.isLoading = false
      }
    },
    toggleHistory(orderId) {
      this.expandedOrderId = this.expandedOrderId === orderId ? null : orderId
    },
    resetSearch() {
      this.search = ''
      this.expandedOrderId = null
      this.fetchDeletedOrders(1)
    },
    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    getLocation(order) {
      const parts = [
        order.governorate && order.governorate.name,
        order.delegation && order.delegation.name,
        order.locality && order.locality.name,
      ].filter(Boolean)
      return parts.join(' / ') || '-'
    },
    isDeleteHistory(history) {
      return String(history.message || '').toLowerCase().includes('supprim')
    },
  },
}
</script>

<style scoped>
.deleted-orders-page {
  padding: 1rem;
}

.toolbar-card,
.deleted-order-card,
.empty-card {
  border-radius: 8px;
}

.toolbar-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.page-title {
  font-weight: 800;
}

.search-box {
  display: flex;
  min-width: 420px;
  gap: 0.5rem;
}

.search-box .form-control,
.search-box .btn {
  height: 42px;
}

.search-box .form-control {
  min-width: 320px;
}

.search-box .btn {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  min-width: 124px;
  padding: 0 1rem;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 180px;
}

.order-row {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) minmax(420px, 1.4fr) auto;
  align-items: center;
  gap: 1rem;
}

.order-title-line {
  display: flex;
  align-items: center;
  font-size: 1rem;
}

.order-meta {
  margin-top: 0.25rem;
}

.external-barcode {
  color: #0a2634;
  font-weight: 600;
}

.external-barcode span {
  color: #6b7280;
  font-weight: 700;
}

.order-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.order-summary small {
  display: block;
  color: #6b7280;
  font-size: 0.75rem;
}

.order-summary strong {
  display: block;
  color: #111827;
}

.history-panel {
  border-top: 1px solid #e5e7eb;
  padding-top: 1rem;
}

.history-item {
  display: grid;
  grid-template-columns: 14px 1fr;
  gap: 0.75rem;
  padding: 0.55rem 0;
}

.history-dot {
  width: 10px;
  height: 10px;
  margin-top: 0.35rem;
  border-radius: 50%;
  background: #7367f0;
}

.history-dot.danger {
  background: #ea5455;
}

.history-title {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-weight: 700;
}

.history-title small,
.history-description {
  color: #6b7280;
}

.history-description {
  margin: 0.25rem 0 0.4rem;
}

.pagination-row {
  margin-top: 1.5rem;
}

@media (max-width: 992px) {
  .toolbar-content,
  .search-box {
    align-items: stretch;
    flex-direction: column;
    min-width: 0;
  }

  .search-box .form-control,
  .search-box .btn {
    min-width: 0;
    width: 100%;
  }

  .order-row,
  .order-summary {
    grid-template-columns: 1fr;
  }
}
</style>
